import { html } from 'lit-element';
import { KatalComponent, Keys, KatLitElement } from '../../shared/base';
import { getSlotValues } from '../../shared/slot-utils';

/**
 * @component {kat-tabs} KatalTabs Use tabs to enable customers an easy way to switch between different sections of information while staying on the same view.
 * @guideline Do Use tabs for organizing discrete blocks of information.
 * @guideline Do Use no more than seven tabs in a tab view to help maintain an uncluttered UI, and reduce cognitive load for customers.
 * @guideline Dont Don't use tabs as steps or navigation.
 * @guideline Dont Don’t hide error messages or critical information inside tabs.
 * @guideline Dont Don't nest tabs.
 * @guideline Dont Don't use tabs to  create flows.
 * @htmlExample - <kat-tab label="tab1" status-label="NEW" tab-id="1">Tab Content Goes Here</kat-tab>
 * @subcomponent ./tabs-tab.js
 * @contentProvided
 * @example SampleTabs {"selected-tab-id": "2", "content": "<kat-tab label=\"tab1\" status-label=\"NEW\" tab-id=\"1\">tab 1 content</kat-tab> <kat-tab label=\"tab2\" tab-id=\"2\">tab 2 content</kat-tab>"}
 * @example Slots {"selected-tab-id": "2", "content": "<kat-tab label=\"tab1\" status-label=\"NEW\" tab-id=\"1\"><span slot=\"status-label\"><kat-icon name=\"ac_unit\" size=\"small\"></kat-icon></span><span slot=\"label\"><kat-icon name=\"account_balance_wallet\" size=\"small\"></kat-icon>Tab One</span>tab 1 content</kat-tab> <kat-tab label=\"tab2\" tab-id=\"2\"><span slot=\"label\"><kat-icon name=\"account_balance\" size=\"small\"></kat-icon>Tab Two</span>tab 2 content</kat-tab>"}
 * @event tab-change Fires when the selected tab has changed. The event detail contains selected-tab which is the target tab to display
 * @status Production
 * @theme flo
 */
export class KatalTabs extends KatLitElement {
  static get properties() {
    return {
      /**
       * @classprop {string} selected-tab-id The name of the tab currently being displayed
       * @required
       */
      selectedTabId: {
        type: String,
        attribute: 'selected-tab-id',
        reflect: true,
      },
    };
  }

  // disable shadow-dom
  createRenderRoot() {
    return this;
  }

  firstUpdated() {
    const config = { childList: true, attributes: true, subtree: true };
    this._observer = new MutationObserver(mutations => {
      this.domChange(mutations);
    });
    this._observer.observe(this, config);

    this.addEventListener('click', event => {
      this.handleTabHeaderClick(event);
    });

    this.addEventListener('keydown', event => {
      if (event.keyCode === Keys.Enter || event.keyCode === Keys.Space) {
        this.handleTabHeaderClick(event);
      }
    });
  }

  updated(changedProperties) {
    if (changedProperties.has('selectedTabId')) {
      this.dispatchCustomEvent(
        'tab-change',
        { 'selected-tab-id': this.selectedTabId },
        false
      );
    }
  }

  render() {
    const tabs = Array.from(this.children).filter(
      child => child.tagName === 'KAT-TAB'
    );

    const tabIdsAdded = {};

    const tabHeadersHtml = tabs.map(newTab => {
      const tabId = newTab.getAttribute('tab-id');

      const slots = getSlotValues(newTab, ['label', 'status-label']);
      const tabLabel = slots['label']
        ? slots['label']
        : newTab.getAttribute('label');
      const statusLabel = slots['status-label']
        ? slots['status-label']
        : newTab.getAttribute('status-label');
      const statusLabelText = statusLabel ? statusLabel : '';
      const tabSelected = tabId === this.selectedTabId;

      if (tabId == null) {
        console.warn(
          "Added a tab to KatalTabs with no tab-id, this won't work right"
        );
      } else if (tabIdsAdded[tabId]) {
        console.warn(
          `Already added ${tabIdsAdded[tabId]} tab(s) with tab-id '${tabId}', this probably won't work right`
        );
        tabIdsAdded[tabId]++;
      } else {
        tabIdsAdded[tabId] = 1;
      }

      return html`
        <kat-tab-header
          tab-id=${tabId}
          tabindex="0"
          class="${tabSelected ? 'tab-selected' : ''}"
        >
          <div>
            <span class="katal-tab-label">${tabLabel}</span>
            <span class="katal-tab-status-label">${statusLabelText}</span>
          </div>
        </kat-tab-header>
      `;
    });

    return html`
      <kat-tab-pane>${tabHeadersHtml}</kat-tab-pane>${tabs.map(tab => {
        if (tab.getAttribute('tab-id') === this.selectedTabId) {
          tab.classList.add('tab-selected');
        } else {
          tab.classList.remove('tab-selected');
        }

        return tab;
      })}
    `;
  }

  handleTabHeaderClick(event) {
    const tabPane = event.target.closest('kat-tab-pane');

    //ensure this click was in the tabContainer
    if (!tabPane) {
      return;
    }

    //ensure nested kat-tabs works
    if (tabPane.parentElement !== this) {
      return;
    }

    //we want to work with the actual tab header that was clicked
    const tabElement = event.target.closest('kat-tab-header');
    const targetTab = tabElement.getAttribute('tab-id');
    if (targetTab === this.selectedTabId) {
      return;
    }

    this.selectedTabId = targetTab;
  }

  domChange(mutations) {
    if (this._domChangeInFlight) {
      return;
    }

    const validMutations = mutations.some(mutation => {
      const isKatTab = node => {
        return node.tagName === 'KAT-TAB';
      };

      // TODO: Not sure how to unit test this filtering.
      // If any of the added or removed nodes are kat-tab, the mutation is valid
      // NodeList are not Arrays and are not filterable by default
      return (
        (mutation.addedNodes &&
          Array.from(mutation.addedNodes).some(isKatTab)) ||
        (mutation.removedNodes &&
          Array.from(mutation.removedNodes).some(isKatTab)) ||
        (mutation.target && isKatTab(mutation.target))
      );
    });

    if (validMutations) {
      this._domChangeInFlight = true;
      this.updateComplete.then(() =>
        // wait for react to finish whatever it's up to
        setTimeout(() => {
          this._domChangeInFlight = false;
        }, 0)
      );
      this.requestUpdate();
    }
  }

  selectTabContainer(newTabId) {
    this.selectTabElement(newTabId, 'kat-tab');
  }

  selectTabButton(newTabId) {
    this.selectTabElement(newTabId, 'kat-tab-header');
  }

  selectTabElement(newTabId, elementType) {
    const newTab = Array.from(this.children).find(
      child =>
        child.tagName === 'KAT-TAB' && child.getAttribute('tab-id') === newTabId
    );

    if (!newTab) {
      throw new Error(
        `Could not find element for selected ${elementType} with ID '${newTabId}'`
      );
    }

    this.selectedTabId = newTabId;
    this.requestUpdate();
  }
}

KatalComponent.register('kat-tabs', KatalTabs);
